import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import type { BorradorPaso2Request, MiRegistroLegalResumen, SepomexEntrada } from "../types";
import { Section, Field, BotonesPaso } from "./Paso1Datos";

const PLACEHOLDER = "(pendiente)";
const CP_PLACEHOLDER = "00000";

/**
 * Paso 2: domicilio fiscal con cascada SEPOMEX. Al teclear un CP de 5
 * dígitos se dispara GET /api/sepomex?cp= y se autocompletan estado/municipio
 * (read-only) además de poblar el dropdown de colonias. Si el CP no
 * devuelve resultados, los campos quedan editables manualmente.
 */
export function Paso2Domicilio({
  rl,
  onSaved,
  onSalir,
  onAtras,
}: {
  rl: MiRegistroLegalResumen;
  onSaved: () => void;
  onSalir: () => void;
  onAtras: () => void;
}) {
  const qc = useQueryClient();

  const limpio = (s: string | null | undefined, ph: string) => (s && s !== ph ? s : "");

  const d = rl.domicilio;
  const [form, setForm] = useState({
    calle: limpio(d?.calle, PLACEHOLDER),
    numeroExterior: limpio(d?.numeroExterior, PLACEHOLDER),
    numeroInterior: d?.numeroInterior ?? "",
    codigoPostal: limpio(d?.codigoPostal, CP_PLACEHOLDER),
    colonia: limpio(d?.colonia, PLACEHOLDER),
    municipio: limpio(d?.municipio, PLACEHOLDER),
    entidadFederativa: limpio(d?.entidadFederativa, PLACEHOLDER),
  });
  const [error, setError] = useState<string | null>(null);

  const qSepomex = useQuery<SepomexEntrada[]>({
    queryKey: ["sepomex", form.codigoPostal],
    enabled: /^[0-9]{5}$/.test(form.codigoPostal),
    queryFn: async () => {
      const { data } = await api.get<SepomexEntrada[]>(`/api/sepomex`, {
        params: { cp: form.codigoPostal },
      });
      return data;
    },
  });

  // Cuando llega el resultado de SEPOMEX, autocompleta estado/municipio si están
  // vacíos y selecciona la primera colonia si la actual no está en la lista.
  // useEffect para no caer en setState durante render.
  useEffect(() => {
    const entradas = qSepomex.data;
    if (!entradas || entradas.length === 0) return;
    setForm((f) => {
      if (f.entidadFederativa !== "" && entradas.some((e) => e.asentamiento === f.colonia)) {
        return f; // ya está aplicado y la colonia actual es válida
      }
      const primera = entradas[0];
      return {
        ...f,
        entidadFederativa: f.entidadFederativa || primera.estado,
        municipio: f.municipio || primera.municipio,
        colonia: entradas.some((e) => e.asentamiento === f.colonia) ? f.colonia : primera.asentamiento,
      };
    });
  }, [qSepomex.data]);

  const guardar = useMutation({
    mutationFn: async () => {
      const body: BorradorPaso2Request = {
        domicilio: {
          calle: form.calle.trim(),
          numeroExterior: form.numeroExterior.trim(),
          numeroInterior: form.numeroInterior?.trim() || null,
          colonia: form.colonia.trim(),
          municipio: form.municipio.trim(),
          entidadFederativa: form.entidadFederativa.trim(),
          codigoPostal: form.codigoPostal.trim(),
          pais: "MEX",
        },
      };
      await api.patch(`/api/mi-registro-legal/borrador/${rl.id}/paso/2`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mi-registro-legal"] });
      onSaved();
    },
    onError: (e: unknown) => {
      const r = e as { response?: { data?: { detail?: string; title?: string } } };
      setError(r.response?.data?.detail || r.response?.data?.title || (e as Error).message);
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^[0-9]{5}$/.test(form.codigoPostal)) {
      setError("El código postal debe tener 5 dígitos.");
      return;
    }
    setError(null);
    guardar.mutate();
  };

  const colonias = qSepomex.data ?? [];
  const sinResultadosCP = /^[0-9]{5}$/.test(form.codigoPostal) && qSepomex.isFetched && colonias.length === 0;

  return (
    <form onSubmit={submit} className="space-y-4">
      <Section title="Domicilio fiscal">
        <Field label="Código postal *">
          <input
            type="text"
            required
            inputMode="numeric"
            pattern="^[0-9]{5}$"
            maxLength={5}
            value={form.codigoPostal}
            onChange={(e) => {
              const val = e.target.value.replace(/[^0-9]/g, "");
              setForm({
                ...form,
                codigoPostal: val,
                // Al cambiar el CP, limpia estado/municipio para forzar refetch
                ...(val !== form.codigoPostal && val.length < 5
                  ? { entidadFederativa: "", municipio: "", colonia: "" }
                  : {}),
              });
            }}
            className="w-full rounded border px-2 py-1.5 font-mono text-sm"
          />
          {qSepomex.isFetching && (
            <p className="mt-1 flex items-center gap-1 text-xs text-fg-subtle">
              <MapPin className="h-3 w-3" /> Buscando colonia…
            </p>
          )}
          {sinResultadosCP && (
            <p className="mt-1 text-xs text-estado-warn">
              No hay coincidencia en SEPOMEX para ese CP. Captura los campos manualmente.
            </p>
          )}
        </Field>
        <Field label="Entidad federativa *">
          <input
            type="text"
            required
            value={form.entidadFederativa}
            onChange={(e) => setForm({ ...form, entidadFederativa: e.target.value })}
            disabled={colonias.length > 0}
            className="w-full rounded border px-2 py-1.5 text-sm disabled:bg-surface-2"
          />
        </Field>
        <Field label="Municipio o demarcación *">
          <input
            type="text"
            required
            value={form.municipio}
            onChange={(e) => setForm({ ...form, municipio: e.target.value })}
            disabled={colonias.length > 0}
            className="w-full rounded border px-2 py-1.5 text-sm disabled:bg-surface-2"
          />
        </Field>
        <Field label="Colonia *" full>
          {colonias.length > 0 ? (
            <select
              required
              value={form.colonia}
              onChange={(e) => setForm({ ...form, colonia: e.target.value })}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            >
              <option value="">— Selecciona colonia —</option>
              {colonias.map((c, i) => (
                <option key={`${c.asentamiento}-${i}`} value={c.asentamiento}>
                  {c.asentamiento}
                  {c.tipoAsenta ? ` (${c.tipoAsenta})` : ""}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              required
              value={form.colonia}
              onChange={(e) => setForm({ ...form, colonia: e.target.value })}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          )}
        </Field>
        <Field label="Calle *" full>
          <input
            type="text"
            required
            value={form.calle}
            onChange={(e) => setForm({ ...form, calle: e.target.value })}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>
        <Field label="Número exterior *">
          <input
            type="text"
            required
            value={form.numeroExterior}
            onChange={(e) => setForm({ ...form, numeroExterior: e.target.value })}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>
        <Field label="Número interior">
          <input
            type="text"
            value={form.numeroInterior ?? ""}
            onChange={(e) => setForm({ ...form, numeroInterior: e.target.value })}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>
      </Section>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-3 text-sm text-estado-warn">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <BotonesPaso
        loading={guardar.isPending}
        labelSiguiente={rl.tipoPersona === "MORAL" ? "Guardar y continuar" : "Guardar y revisar"}
        onSalir={onSalir}
        onAtras={onAtras}
      />
    </form>
  );
}
